import {
  switchEnterpriseResponseSchema,
  type SwitchEnterpriseResponse,
  type WorkosLoginResponse,
} from "@mg/schemas/src/prince/auth";

/**
 * The MFA step arm of a `POST /enterprise/switch` response.
 *
 * A per-enterprise MFA tenant answers the switch with the WorkOS challenge
 * instead of a session, so the caller has to prove a TOTP code before the
 * switch happens.
 *
 * why: NO producer emits this arm at this head — master's switch route returns
 * `{ token }` unconditionally and `switchEnterpriseResponseSchema` is still
 * success-only. It is a forward-compatible branch for #6156, whose christo
 * switch route answers a proof-less switch into a flagged enterprise with this
 * payload as a 200 from `startEnterpriseMfaStep`
 * (`services/christo/app/controllers/prince-auth/handlers/enterpriseMfaStep.ts`),
 * and whose `switchEnterpriseResponseSchema` gains it as a union arm. Do not
 * read this type as evidence a producer exists on master.
 *
 * why: the challenge and factor are optional HERE though `isEnterpriseMfaStep`
 * requires the challenge. The type tracks the schema's deepPartial arm — under
 * #6156 `readSwitchEnterpriseResponse` returns that arm's inferred type — so it
 * must stay assignable from it. The GUARD is what narrows to a challenge-bearing
 * step: `mfaOptionsOf` feeds `authenticationChallenge.id` into `POST /workos-mfa`
 * as a required body field, so a challenge-less body is not a steppable switch.
 *
 * This module is a leaf on purpose. `services/auth.ts` reads it to classify the
 * switch response, and `utils/enterpriseSwitch.ts` reads it to park the step, so
 * it must not reach back into either (that direction is the store-cycle the
 * `switchEnterpriseWithMfa` why-comment records).
 */
export type EnterpriseMfaStep = {
  enterpriseMfaRequired: true;
  enterpriseId: string;
  pendingAuthenticationToken: string;
} & Pick<
  WorkosLoginResponse,
  "authenticationChallenge" | "authenticationFactor"
>;

/**
 * Is this switch response the MFA step arm?
 *
 * Takes `unknown` on purpose: the response is read before any schema claims it,
 * so there is no concrete type to narrow from. `=== true` (not a truthy read)
 * is what keeps a success arm carrying an unrelated `enterpriseMfaRequired`
 * field from being mistaken for a step.
 */
export function isEnterpriseMfaStep(res: unknown): res is EnterpriseMfaStep {
  if (typeof res !== "object" || res === null) return false;
  const step = res as Partial<EnterpriseMfaStep>;
  return (
    step.enterpriseMfaRequired === true &&
    typeof step.enterpriseId === "string" &&
    typeof step.pendingAuthenticationToken === "string" &&
    typeof step.authenticationChallenge?.id === "string"
  );
}

/**
 * The WorkOS options `AuthenticationDialog` needs to render the TOTP step.
 * `enterpriseMfaRequired` and `enterpriseId` are dropped here: the dialog
 * verifies the code against the pending token, which already names the
 * enterprise the switch is completing.
 */
export function mfaOptionsOf(step: EnterpriseMfaStep): WorkosLoginResponse {
  return {
    pendingAuthenticationToken: step.pendingAuthenticationToken,
    authenticationChallenge: step.authenticationChallenge,
    authenticationFactor: step.authenticationFactor,
  };
}

/**
 * Classify a switch response into the arm the caller has to handle.
 *
 * why: the schema is validated BEFORE the step arm is recognised, because
 * `switchEnterprise` used to parse first — a step-shaped payload then threw a
 * `ZodError` inside the service, so no caller could ever see the step and the
 * switch died as a validation error instead of opening the TOTP step. A
 * response is a step only when it carries the literal discriminator plus a
 * string `enterpriseId`, a string `pendingAuthenticationToken` and a string
 * `authenticationChallenge.id` — the three fields the TOTP step is verified
 * against; every other body still throws the schema's own error.
 *
 * The step arm is read by shape rather than by the schema's type so this
 * compiles both before and after the schema carries the arm: the schema's
 * success branch takes it once the arm is declared, and the guard takes it
 * until then.
 */
export function readSwitchEnterpriseResponse(
  json: unknown,
): SwitchEnterpriseResponse | EnterpriseMfaStep {
  const parsed = switchEnterpriseResponseSchema.safeParse(json);
  if (parsed.success) return parsed.data;
  if (isEnterpriseMfaStep(json)) return json;
  throw parsed.error;
}
